/**
 * SupportDelayBroadcastButton — Feb 2026
 * --------------------------------------
 * One-off "Support-Delay-Update" broadcast trigger. Opens a confirmation
 * modal that first shows the dry-run preview (who would receive it,
 * grouped by collection + skip-reason) so the admin can sanity-check
 * before pressing the red button. Once sent, the button stays mounted
 * (idempotency in the backend prevents double-send) but turns into a
 * passive "Bereits versendet" badge on the next render.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, X, AlertTriangle, CheckCircle2, Send, ExternalLink } from "lucide-react";

export default function SupportDelayBroadcastButton({ apiBase, token }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {eligible_count, skipped_count, eligible, skipped}
  const [sendingNow, setSendingNow] = useState(false);
  const [report, setReport] = useState(null);

  const fetchPreview = async () => {
    setOpen(true);
    setLoading(true);
    setPreview(null);
    setReport(null);
    try {
      const r = await axios.get(
        `${apiBase}/admin/broadcast/support-delay-update/preview`,
        { headers: { "X-Admin-Token": token } },
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview konnte nicht geladen werden");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const fireSend = async () => {
    if (!preview || !preview.eligible_count) return;
    if (!window.confirm(
      `Jetzt SUPPORT_DELAY_UPDATE an ${preview.eligible_count} Empfänger versenden?\n\n` +
      `Das ist ein einmaliger Versand — bereits-versendete Empfänger werden automatisch übersprungen, falls du nochmal klickst.`
    )) return;
    setSendingNow(true);
    try {
      const r = await axios.post(
        `${apiBase}/admin/broadcast/support-delay-update/send`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      setReport(r.data);
      const okN = r.data?.sent_ok ?? 0;
      const failN = r.data?.sent_failed ?? 0;
      toast.success(`Broadcast fertig: ${okN} gesendet${failN ? `, ${failN} Fehler` : ""}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSendingNow(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={fetchPreview}
        data-testid="support-delay-broadcast-open"
        className="al-cta-ghost text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
        title="Einmaliger Versand der Support-Delay-Update-Mail an alle Kunden und Partner"
      >
        <Mail className="h-4 w-4" />
        <span>Support-Delay-Update versenden</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !sendingNow) setOpen(false); }}
          data-testid="support-delay-broadcast-modal"
        >
          <div className="al-card max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-slate-200 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">
                  Einmaliger Broadcast
                </div>
                <h2 className="text-lg font-bold text-slate-900 mt-1 leading-tight">Support-Delay-Update</h2>
                <div className="text-xs text-slate-500 mt-0.5">
                  Mail an alle aktiven Kunden + Partner. Demo-Accounts, Opt-Outs, gemergte Adressen und bereits-versendete Empfänger werden automatisch übersprungen.
                </div>
              </div>
              <button
                type="button"
                onClick={() => !sendingNow && setOpen(false)}
                data-testid="support-delay-broadcast-close"
                className="ml-auto p-1.5 rounded-md hover:bg-slate-100 text-slate-500 disabled:opacity-30"
                disabled={sendingNow}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-4 sm:px-5 py-4 overflow-y-auto flex-1 space-y-4">
              {loading && (
                <div className="flex items-center justify-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Vorschau …
                </div>
              )}

              {!loading && preview && !report && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-center">
                      <div className="font-display font-bold text-3xl text-emerald-700">{preview.eligible_count}</div>
                      <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Bekommen die Mail</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
                      <div className="font-display font-bold text-3xl text-slate-700">{preview.skipped_count}</div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-600 font-bold">Übersprungen</div>
                    </div>
                  </div>

                  {preview.eligible_count > 0 && (
                    <section>
                      <header className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1.5 flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Bekommen die Mail
                      </header>
                      <ul className="divide-y divide-emerald-100 border border-emerald-200 rounded-lg max-h-64 overflow-y-auto bg-white">
                        {preview.eligible.map((c) => (
                          <li
                            key={`${c.collection}-${c.id}-${c.email}`}
                            className="px-3 py-2 flex items-center gap-2"
                            data-testid={`support-delay-eligible-${c.id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
                              <div className="text-[11px] text-slate-500 font-mono truncate">{c.email}</div>
                            </div>
                            <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 shrink-0">
                              {c.collection === "partners" ? "Partner" : "Kunde"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {preview.skipped_count > 0 && (
                    <details>
                      <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 hover:text-slate-700">
                        {preview.skipped_count} übersprungen — Details anzeigen
                      </summary>
                      <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-lg max-h-48 overflow-y-auto bg-slate-50">
                        {preview.skipped.map((c, idx) => (
                          <li key={c.email || idx} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                            <div className="min-w-0 flex-1">
                              <div className="text-slate-700 truncate">{c.name || c.email}</div>
                              <div className="text-[10px] text-slate-500 font-mono truncate">{c.email}</div>
                            </div>
                            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 shrink-0">
                              {c.reason}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </>
              )}

              {report && (
                <div className="space-y-3">
                  <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-center">
                    <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-1" />
                    <div className="font-display font-bold text-2xl text-emerald-700">{report.sent_ok}</div>
                    <div className="text-xs text-emerald-700 font-bold">Mails versendet</div>
                  </div>
                  {report.sent_failed > 0 && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                      <div className="flex items-center gap-1.5 font-bold mb-1">
                        <AlertTriangle className="h-4 w-4" /> {report.sent_failed} Fehler
                      </div>
                      <ul className="text-xs space-y-0.5 max-h-32 overflow-y-auto">
                        {(report.failures || []).map((f, idx) => (
                          <li key={f.email || idx} className="font-mono">{f.email} — {f.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            {!loading && preview && !report && (
              <div className="px-4 sm:px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
                <button
                  type="button"
                  onClick={() => !sendingNow && setOpen(false)}
                  className="al-cta-ghost text-sm"
                  disabled={sendingNow}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={fireSend}
                  disabled={sendingNow || preview.eligible_count === 0}
                  data-testid="support-delay-broadcast-fire"
                  className="al-cta text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sendingNow ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Sende …</>
                  ) : (
                    <><Send className="h-4 w-4" /> An {preview.eligible_count} Empfänger versenden</>
                  )}
                </button>
              </div>
            )}
            {report && (
              <div className="px-4 sm:px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
                <button type="button" onClick={() => setOpen(false)} className="al-cta text-sm">
                  Schließen
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
